import { SkeletonTable, Spinner } from '@aurora/ui-kit';
import { Suspense, lazy, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router';

import { useMe } from './auth/useAuth.js';
import { useModules } from './modules/useModules.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { LoginPage } from './pages/LoginPage.js';
import { ModulesPage } from './pages/ModulesPage.js';
import { TenantProfilePage } from './pages/TenantProfilePage.js';
import CustomFieldsPage from './pages/CustomFieldsPage.js';
import AutomationsPage from './pages/AutomationsPage.js';
import TagsPage from './pages/TagsPage.js';
import PortalCustomersPage from './pages/PortalCustomersPage.js';
import RolesPage from './pages/RolesPage.js';
import AuditPage from './pages/AuditPage.js';
import ReportsPage from './pages/ReportsPage.js';
import AnalyticsPage from './pages/AnalyticsPage.js';
import ControllingPage from './pages/ControllingPage.js';
import ProfilePage from './pages/ProfilePage.js';
import { UsersPage } from './pages/UsersPage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';
import { AppShell } from './shell/AppShell.js';

// v6/E3: Baukasten — eigener Chunk, nur auf der Agentur-Instanz erreichbar
const BuilderPage = lazy(() => import('./builder/BuilderPage.js'));
import { useFeatures } from './state/useFeatures.js';

/**
 * Routing der Shell. Modul-Routen entstehen dynamisch aus den geladenen
 * Frontend-Modulen und sind lazy — jedes Modul ist ein eigener Chunk,
 * geladen nur bei Aktivierung UND Aufruf.
 */
function ProtectedApp() {
  const me = useMe();
  const modules = useModules();

  const moduleRoutes = useMemo(
    () =>
      (modules.data?.frontendModules ?? []).flatMap((frontendModule) =>
        frontendModule.routes.map((route) => {
          const LazyPage = lazy(route.component);
          const fullPath = `/m/${frontendModule.name}/${route.path}`.replace(/\/+$/, '');
          return (
            <Route
              key={fullPath}
              path={fullPath}
              element={
                <Suspense
                  fallback={
                    <div className="page">
                      <SkeletonTable rows={6} />
                    </div>
                  }
                >
                  <LazyPage />
                </Suspense>
              }
            />
          );
        }),
      ),
    [modules.data?.frontendModules],
  );

  const { has: hasFeature } = useFeatures();

  if (me.isLoading) {
    return (
      <div className="app-loading">
        <Spinner label="Anmeldung wird geprüft …" />
      </div>
    );
  }
  if (!me.data) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Routes>
      {/* v6/E3: Vollbild-Seite AUSSERHALB der AppShell — der Baukasten hat
          seine eigene Bühne; das Instanz-Theming der Shell bleibt draußen. */}
      {me.data.builder === true ? (
        <Route
          path="/erzeugen"
          element={
            <Suspense
              fallback={
                <div className="app-loading">
                  <Spinner label="Baukasten wird geladen …" />
                </div>
              }
            >
              <BuilderPage />
            </Suspense>
          }
        />
      ) : null}
      <Route element={<AppShell me={me.data} />}>
        <Route index element={<DashboardPage />} />
        <Route path="/einstellungen/module" element={<ModulesPage />} />
        <Route path="/einstellungen/stammdaten" element={<TenantProfilePage />} />
        <Route path="/einstellungen/benutzer" element={<UsersPage />} />
        <Route path="/einstellungen/felder" element={<CustomFieldsPage />} />
        {hasFeature('automations') ? (
          <Route path="/einstellungen/automationen" element={<AutomationsPage />} />
        ) : null}
        {hasFeature('tags') ? <Route path="/einstellungen/tags" element={<TagsPage />} /> : null}
        {hasFeature('portal') ? (
          <Route path="/einstellungen/kundenportal" element={<PortalCustomersPage />} />
        ) : null}
        <Route path="/einstellungen/rollen" element={<RolesPage />} />
        {hasFeature('audit') ? <Route path="/einstellungen/audit" element={<AuditPage />} /> : null}
        {hasFeature('reports') ? <Route path="/berichte" element={<ReportsPage />} /> : null}
        {hasFeature('analytics') ? (
          <Route path="/auswertungen" element={<AnalyticsPage />} />
        ) : null}
        {hasFeature('controlling') ? (
          <Route path="/controlling" element={<ControllingPage />} />
        ) : null}
        <Route path="/einstellungen/profil" element={<ProfilePage />} />
        {moduleRoutes}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

/**
 * v5/O8 Kundenportal — eigener Zweig NEBEN der Mitarbeiter-App: eigene Session
 * (aurora_portal_sid), keine AppShell, kein useMe. Lazy, damit Kunden nur ihren
 * eigenen Chunk laden.
 */
const PortalLoginPage = lazy(() => import('./portal/PortalLoginPage.js'));
const PortalPage = lazy(() => import('./portal/PortalPage.js'));

function PortalApp() {
  return (
    <Suspense
      fallback={
        <div className="app-loading">
          <Spinner label="Wird geladen …" />
        </div>
      }
    >
      {/* Pfade sind RELATIV zu /portal/* — verschachtelte Routes matchen den Restpfad. */}
      <Routes>
        <Route index element={<PortalPage />} />
        <Route path="login" element={<PortalLoginPage />} />
        <Route path="*" element={<Navigate to="/portal" replace />} />
      </Routes>
    </Suspense>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/portal/*" element={<PortalApp />} />
      <Route path="*" element={<ProtectedApp />} />
    </Routes>
  );
}
